// -----------------------------------------------------------------------------
// Deterministic mock data for the Vehicle Racking Manager page.
//
// The yard-side API doesn't currently expose racking-specific endpoints
// (see the new `/racking/*` entries in `docs/pending-endpoints.md`), so we
// seed a curated set of racking bays and 30 vehicles assigned across them.
// The same PRNG approach we use for the Reports pages (mulberry32) keeps
// the numbers stable across renders.
// -----------------------------------------------------------------------------

export function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let x = t;
    x = Math.imul(x ^ (x >>> 15), x | 1);
    x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

export interface RackingBay {
  id: string;
  name: string;
  /** Physical zone, e.g. "North Yard" or "Hangar B". */
  zone: string;
  /** Number of vehicle-sized spaces the bay contains. */
  capacity: number;
  /** Bay type — helps the yard team route cars correctly. */
  kind: "Upper Rack" | "Ground Level" | "Lift Stand";
  notes?: string;
}

export const RACKING_BAYS: RackingBay[] = [
  { id: "bay-A1", name: "A1", zone: "North Yard",  capacity: 4, kind: "Upper Rack",   notes: "Priority dismantle row" },
  { id: "bay-A2", name: "A2", zone: "North Yard",  capacity: 4, kind: "Upper Rack" },
  { id: "bay-A3", name: "A3", zone: "North Yard",  capacity: 3, kind: "Upper Rack",   notes: "Heavier vehicles only" },
  { id: "bay-B1", name: "B1", zone: "Hangar B",    capacity: 6, kind: "Ground Level", notes: "Under cover" },
  { id: "bay-B2", name: "B2", zone: "Hangar B",    capacity: 6, kind: "Ground Level" },
  { id: "bay-B3", name: "B3", zone: "Hangar B",    capacity: 5, kind: "Ground Level" },
  { id: "bay-C1", name: "C1", zone: "South Yard",  capacity: 4, kind: "Upper Rack" },
  { id: "bay-C2", name: "C2", zone: "South Yard",  capacity: 4, kind: "Upper Rack" },
  { id: "bay-D1", name: "D1", zone: "Workshop",    capacity: 2, kind: "Lift Stand",   notes: "Active dismantle lift" },
  { id: "bay-D2", name: "D2", zone: "Workshop",    capacity: 2, kind: "Lift Stand" },
];

export interface RackedVehicle {
  id: string;
  bayId: string;
  bayPosition: number; // 1-based slot within the bay
  stockNumber: string;
  registration: string;
  make: string;
  model: string;
  derivative: string;
  year: number;
  colour: string;
  mileage: number;
  fuelType: "Petrol" | "Diesel" | "Hybrid" | "Electric";
  bodyStyle: string;
  /** When the vehicle was racked / arrived in its bay. */
  rackedOn: string; // ISO date
  /** Days the vehicle has sat in racking as of now. */
  daysInRacking: number;
  imageUrl: string;
  partsSaleable: number;
  partsListed: number;
  partsSold: number;
  partsReserved: number;
  /** Estimated retail value of still-available parts on the vehicle. */
  potentialValue: number;
  jobsPending: number;
  jobsInProgress: number;
  /** "Awaiting Dismantling", "In Progress" or "Ready to Clear". */
  status: "Awaiting Dismantling" | "In Progress" | "Ready to Clear";
  priority: "Low" | "Normal" | "High" | "Urgent";
  lastActivity: string; // ISO date (last job/part activity)
}

const MAKES: {
  make: string;
  models: { model: string; derivatives: string[] }[];
}[] = [
  { make: "Ford",        models: [{ model: "Kuga",     derivatives: ["ST-Line X 2.0 EcoBlue", "Titanium 1.5 EcoBoost"] }, { model: "Fiesta", derivatives: ["Zetec 1.0 EcoBoost", "ST-Line 1.5"] }] },
  { make: "Volkswagen",  models: [{ model: "Golf",     derivatives: ["GT TSI EVO 150", "R-Line TDI 115"] }, { model: "Passat", derivatives: ["GT 2.0 TDI 150"] }] },
  { make: "BMW",         models: [{ model: "3 Series", derivatives: ["320d M Sport xDrive"] }, { model: "1 Series", derivatives: ["118i M Sport"] }] },
  { make: "Audi",        models: [{ model: "A3",       derivatives: ["Sportback 35 TFSI"] }, { model: "Q5", derivatives: ["TFSI Quattro S Line"] }] },
  { make: "Nissan",      models: [{ model: "Qashqai",  derivatives: ["Tekna DIG-T 158", "N-Connecta"] }] },
  { make: "Vauxhall",    models: [{ model: "Astra",    derivatives: ["Elite Nav 1.4T", "SRi 1.6"] }] },
  { make: "Mercedes",    models: [{ model: "A-Class",  derivatives: ["A 180 AMG Line Exec"] }] },
  { make: "Land Rover",  models: [{ model: "Discovery Sport", derivatives: ["HSE SD4 TD"] }] },
  { make: "Hyundai",     models: [{ model: "Tucson",   derivatives: ["Premium SE MHEV"] }] },
  { make: "Kia",         models: [{ model: "Sportage", derivatives: ["GT-Line S 1.6 CRDi"] }] },
  { make: "Toyota",      models: [{ model: "Auris",    derivatives: ["Icon Hybrid"] }] },
  { make: "Mini",        models: [{ model: "Cooper S", derivatives: ["Sport 3-Door"] }] },
  { make: "Volvo",       models: [{ model: "XC60",     derivatives: ["R-Design B4 AWD"] }] },
];

const COLOURS = [
  "Magnetic Grey",
  "Tornado Red",
  "Alpine White",
  "Mythos Black",
  "Ceramic Grey",
  "Cosmos Black",
  "Fuji White",
  "Phantom Black",
  "Infra Red",
  "Atlantic Blue",
  "Mineral Grey",
  "Glacier White",
  "Pearl White",
  "Midnight Silver",
  "Shadow Black",
];

const FUELS: RackedVehicle["fuelType"][] = [
  "Petrol",
  "Diesel",
  "Petrol",
  "Petrol",
  "Diesel",
  "Hybrid",
  "Electric",
];

// Small unsplash-hosted stock images, chunked with cars of approximately
// the right shape. These are stable URLs; we won't hotlink heavy hero
// images — everything is `?w=400` for quick loading in table cells.
const STOCK_IMAGES = [
  "https://images.unsplash.com/photo-1583121274602-3e2820c69888?auto=format&fit=crop&w=400&q=60",
  "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=400&q=60",
  "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?auto=format&fit=crop&w=400&q=60",
  "https://images.unsplash.com/photo-1617654112368-307921291f42?auto=format&fit=crop&w=400&q=60",
  "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?auto=format&fit=crop&w=400&q=60",
  "https://images.unsplash.com/photo-1547038577-da80abbc4f19?auto=format&fit=crop&w=400&q=60",
  "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=400&q=60",
  "https://images.unsplash.com/photo-1511919884226-fd3cad34687c?auto=format&fit=crop&w=400&q=60",
  "https://images.unsplash.com/photo-1555215695-3004980ad54e?auto=format&fit=crop&w=400&q=60",
  "https://images.unsplash.com/photo-1541899481282-d53bffe3c35d?auto=format&fit=crop&w=400&q=60",
  "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&w=400&q=60",
  "https://images.unsplash.com/photo-1580273916550-e323be2ae537?auto=format&fit=crop&w=400&q=60",
];

/** Make a semi-random UK plate that visually looks plausible. */
function plate(rand: () => number): string {
  const letters = "ABCDEFGHJKLMNOPRSTUVWXYZ";
  const pick = (s: string) => s.charAt(Math.floor(rand() * s.length));
  const yr = Math.floor(rand() * 20) + 5; // 05..24
  return `${pick(letters)}${pick(letters)}${yr.toString().padStart(2, "0")} ${pick(letters)}${pick(letters)}${pick(letters)}`;
}

/**
 * Deterministically place ~30 vehicles across the racking bays so the page
 * shows a realistic mix of fully-loaded, half-full, and nearly-empty bays.
 */
export function buildRackedVehicles(seed = 0xBAAB1E): RackedVehicle[] {
  const rand = mulberry32(seed);

  // Build the pool of (bayId, slot) tuples to fill. We occupy a randomised
  // subset of the total capacity so some bays are full and some have spaces.
  const slots: { bayId: string; slot: number }[] = [];
  for (const bay of RACKING_BAYS) {
    for (let i = 1; i <= bay.capacity; i++) {
      slots.push({ bayId: bay.id, slot: i });
    }
  }
  // Shuffle slots deterministically.
  for (let i = slots.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [slots[i], slots[j]] = [slots[j], slots[i]];
  }

  // Fill ~78% of slots so we have some headroom. This is ~32 vehicles
  // which nicely matches the fixture expectation.
  const fillCount = Math.floor(slots.length * 0.78);
  const toFill = slots.slice(0, fillCount);

  const vehicles: RackedVehicle[] = toFill.map((slot, i) => {
    const makeRow = MAKES[Math.floor(rand() * MAKES.length)];
    const modelRow =
      makeRow.models[Math.floor(rand() * makeRow.models.length)];
    const derivative =
      modelRow.derivatives[
        Math.floor(rand() * modelRow.derivatives.length)
      ];
    const year = 2014 + Math.floor(rand() * 11); // 2014..2024
    const colour = COLOURS[Math.floor(rand() * COLOURS.length)];
    const mileage = Math.floor(8_000 + rand() * 140_000);
    const fuelType = FUELS[Math.floor(rand() * FUELS.length)];
    const daysInRacking = Math.floor(rand() * 120); // 0..120 days
    const rackedMs = Date.now() - daysInRacking * 86_400_000;

    const partsSaleable = Math.floor(rand() * 40) + 5;
    const partsListed = Math.min(
      partsSaleable,
      Math.floor(partsSaleable * (0.4 + rand() * 0.55))
    );
    const partsSold = Math.min(
      partsListed,
      Math.floor(partsListed * (rand() * 0.6))
    );
    const partsReserved = Math.min(
      partsListed - partsSold,
      Math.floor(rand() * 5)
    );
    const potentialValue = Math.round(
      300 + rand() * 4800 + partsSaleable * (20 + rand() * 60)
    );

    const jobsPending = Math.floor(rand() * 4);
    const jobsInProgress = rand() < 0.25 ? 1 : 0;

    // Status correlates with jobs and ageing so the page tells a story:
    // fresh arrivals default to "Awaiting Dismantling", actively-worked
    // vehicles to "In Progress", and very old ones end up "Ready to Clear".
    let status: RackedVehicle["status"] = "Awaiting Dismantling";
    if (jobsInProgress > 0) status = "In Progress";
    else if (daysInRacking > 80 && partsSaleable <= partsSold + partsReserved + 2) {
      status = "Ready to Clear";
    }

    // Priority: newer + pricier + more jobs pending tend to be urgent.
    const pr = rand();
    let priority: RackedVehicle["priority"] = "Normal";
    if (pr < 0.08) priority = "Urgent";
    else if (pr < 0.28) priority = "High";
    else if (pr > 0.85) priority = "Low";

    const lastActivityDays = Math.max(
      0,
      Math.floor(daysInRacking * rand())
    );

    return {
      id: `rv-${(i + 1).toString().padStart(3, "0")}`,
      bayId: slot.bayId,
      bayPosition: slot.slot,
      stockNumber: `WM-${(23800 + i * 3).toString()}`,
      registration: plate(rand),
      make: makeRow.make,
      model: modelRow.model,
      derivative,
      year,
      colour,
      mileage,
      fuelType,
      bodyStyle: "SUV",
      rackedOn: new Date(rackedMs).toISOString(),
      daysInRacking,
      imageUrl: STOCK_IMAGES[i % STOCK_IMAGES.length],
      partsSaleable,
      partsListed,
      partsSold,
      partsReserved,
      potentialValue,
      jobsPending,
      jobsInProgress,
      status,
      priority,
      lastActivity: new Date(
        Date.now() - lastActivityDays * 86_400_000
      ).toISOString(),
    };
  });

  // Sort so the most-urgent, longest-resident vehicles are at the top by
  // default. The page itself lets the user re-sort if they want.
  const priorityRank: Record<RackedVehicle["priority"], number> = {
    Urgent: 0,
    High: 1,
    Normal: 2,
    Low: 3,
  };
  vehicles.sort((a, b) => {
    const pr = priorityRank[a.priority] - priorityRank[b.priority];
    if (pr !== 0) return pr;
    return b.daysInRacking - a.daysInRacking;
  });

  return vehicles;
}

export interface BayOccupancy {
  bay: RackingBay;
  vehicles: RackedVehicle[];
  used: number;
  available: number;
  utilisation: number; // 0..100
  avgDays: number;
  oldestDays: number;
}

export function summariseBays(vehicles: RackedVehicle[]): BayOccupancy[] {
  const byBay = new Map<string, RackedVehicle[]>();
  for (const bay of RACKING_BAYS) byBay.set(bay.id, []);
  for (const v of vehicles) {
    const list = byBay.get(v.bayId);
    if (list) list.push(v);
  }

  return RACKING_BAYS.map((bay) => {
    const occ = byBay.get(bay.id) ?? [];
    const used = occ.length;
    const available = Math.max(0, bay.capacity - used);
    const utilisation =
      bay.capacity === 0 ? 0 : Math.round((used / bay.capacity) * 100);
    const avgDays =
      used === 0
        ? 0
        : Math.round(
            occ.reduce((s, v) => s + v.daysInRacking, 0) / used
          );
    const oldestDays = occ.reduce(
      (m, v) => Math.max(m, v.daysInRacking),
      0
    );
    return {
      bay,
      vehicles: occ.sort((a, b) => a.bayPosition - b.bayPosition),
      used,
      available,
      utilisation,
      avgDays,
      oldestDays,
    };
  });
}
